<template>
  <div
    class="c-slider"
    :class="{ insideArrows, hasMultiColumns }"
    :style="style"
  >
    <div class="c-slider__slider" ref="slider">
      <slot></slot>
    </div>

    <button
      type="button"
      :class="['c-slider__prev', { withPinkBack, disabled: !hasPrev }]"
      ref="prev"
      @click="emitPrevIndex"
    ></button>
    <button
      type="button"
      :class="['c-slider__next', { withPinkBack, disabled: !hasNext }]"
      ref="next"
      @click.prevent.stop="emitNextIndex"
    ></button>

    <div class="c-slider__current-n" v-if="showCurrentN">
      {{ indexCurrent + 1 }} / {{ slideAmount }}
    </div>
  </div>
</template>

<script>
import 'slick-carousel'
import { nextTick } from 'vue'
const SAFETY_TIME = 500 // ミリ秒
window.timer = null

export default {
  name: 'ComponentSlider',
  props: {
    columns: { type: Number, default: 4 },
    height: { type: Number, default: null },
    insideArrows: { type: Boolean, default: false },
    isInfinite: { type: Boolean, default: true },
    rows: { type: Number, default: 1 },
    showCurrentN: { type: Boolean, default: false },
    sliderItemsChanged: { type: Boolean, default: false },
    slickGoTo: { type: Number, default: null },
    slideAmount: { type: Number, default: Number.MAX_SAFE_INTEGER },
    withPinkBack: { type: Boolean, default: false },
  },
  data() {
    return {
      isInitialized: false,
      indexCurrent: 0,
    }
  },
  computed: {
    hasNext() {
      return (
        this.isInfinite || this.indexCurrent + this.columns < this.slideAmount
      )
    },
    hasPrev() {
      return this.isInfinite || 0 < this.indexCurrent
    },
    hasMultiColumns() {
      return this.columns > 1
    },
    style() {
      return this.height ? { height: `${this.height}px` } : undefined
    },
  },
  watch: {
    slickGoTo(index) {
      if (Number.isInteger(index)) {
        $(this.$refs.slider).slick('slickGoTo', index)
        this.indexCurrent = index
      }
    },
    sliderItemsChanged(sliderItemsChanged) {
      if (sliderItemsChanged) {
        if (this.isInitialized) {
          $(this.$refs.slider).slick('unslick')
        }
        this.initializeSlider()
        this.setCardsHeight()
        $(this.$refs.slider).slick('slickGoTo', 0)
        this.$emit('slider-set-completed')
      }
    },
  },
  mounted() {
    this.setCardsHeight()
    $(this.$refs.slider).on('afterChange', (event, slick, currentSlide) => {
      this.$emit('slider-changed', currentSlide)
    })
    if (this.sliderItemsChanged) this.initializeSlider()
  },
  methods: {
    emitPrevIndex() {
      if (window.timer) return
      const indexCurrent = $(this.$refs.slider).slick('slickCurrentSlide')
      if (this.isInfinite || 0 < indexCurrent) {
        this.indexCurrent = indexCurrent - 1
        this.$emit('current-index-changed', this.indexCurrent)
      }
      // 高速で < > ボタンをクリックするとスライドしなくなってしまうので、少し待つ
      window.timer = setTimeout(() => {
        window.timer = null
      }, SAFETY_TIME)
    },
    emitNextIndex() {
      if (window.timer) return
      const indexCurrent = $(this.$refs.slider).slick('slickCurrentSlide')
      if (this.isInfinite || indexCurrent + this.columns < this.slideAmount) {
        this.indexCurrent = indexCurrent + 1
        this.$emit('current-index-changed', this.indexCurrent)
      }
      // 高速で < > ボタンをクリックするとスライドしなくなってしまうので、少し待つ
      window.timer = setTimeout(() => {
        window.timer = null
      }, SAFETY_TIME)
    },
    initializeSlider() {
      $(this.$refs.slider).slick({
        infinite: this.isInfinite,
        rows: this.rows,
        slidesToShow: this.columns,
        prevArrow: this.isInfinite ? $(this.$refs.prev) : null,
        nextArrow: this.isInfinite ? $(this.$refs.next) : null,
      })
      this.isInitialized = true
    },
    async setCardsHeight() {
      const elementsCard = this.$el.querySelectorAll('.c-product-card')
      let maxHeight = 0
      await nextTick(() => {
        maxHeight = Array.from(elementsCard).reduce((maxHeight, element) => {
          element.style.height = 'auto'
          return Math.max(element.clientHeight, maxHeight)
        }, 0)
      })
      elementsCard.forEach((element) => {
        element.style.height = `${maxHeight}px`
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '~/scss/generic/variables';

.c-slider {
  padding: 0 50px;
  position: relative;

  &.insideArrows {
    padding: 0;
  }

  &__prev,
  &__next {
    background-color: rgba(variables.$color-pink, 0.5);
    border-radius: 22px;
    border: 1px solid variables.$color-pink;
    cursor: pointer;
    height: 44px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition: 0.3s;
    width: 44px;

    &:hover {
      background-color: variables.$color-pink;
    }

    &::before {
      content: '';
      border-right: 1px solid variables.$color-brown;
      border-bottom: 1px solid variables.$color-brown;
      display: block;
      height: 8px;
      position: absolute;
      right: 45%;
      top: 45%;
      width: 8px;
    }

    &.disabled,
    &.slick-disabled {
      background-color: variables.$color-gray;
      border-color: variables.$color-gray;
      color: variables.$color-text-gray;
      cursor: default;

      &:hover {
        opacity: 1;
      }
    }

    &.withPinkBack {
      background-color: rgba(#fff, 0.8);
    }
  }

  &__prev {
    left: 1px;

    &::before {
      transform: rotate(135deg) translate(-50%);
    }
  }

  &__next {
    right: 1px;

    &::before {
      transform: rotate(-45deg) translate(50%);
    }
  }

  &__current-n {
    background-color: rgba(variables.$color-pink, 0.5);
    border-radius: 4px;
    bottom: 5px;
    color: variables.$color-brown;
    left: 50%;
    margin: auto;
    padding: 5px 10px;
    position: absolute;
    transform: translateX(-50%);
  }
}
</style>

<style lang="scss">
.c-slider {
  &.hasMultiColumns .slick-slide {
    padding: 0 16px;
  }

  .slick-slide > div + div {
    margin-top: 32px;
  }
}
</style>
